'use client';
import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Menu, X, ArrowRight } from 'lucide-react';
import { Logo } from '@/components/ui/logo';
import { authClient } from '@/lib/auth/client';
import { useRouter } from 'next/navigation';

const navLinks = [
  { label: 'How It Works', href: '/how-it-works' },
  { label: 'Ecosystem', href: '/ecosystem' },
  { label: 'Founders', href: '/founders' },
  { label: 'Investors', href: '/investors' },
  { label: 'Consultants', href: '/consultants' },
  { label: 'Fresher Graduates', href: '/fresher-graduates' },
];

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [session, setSession] = useState<{ user: { role?: string } } | null>(
    null
  );
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const router = useRouter();

  // Check for authenticated session
  useEffect(() => {
    authClient
      .getSession()
      .then((res) => {
        setSession(res.data ?? null);
      })
      .finally(() => {
        setIsCheckingSession(false);
      });
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  // Get dashboard URL based on user role
  const getDashboardUrl = () => {
    if (!session?.user?.role) return null;
    const role = session.user.role;
    if (role === 'admin') return '/admin/dashboard';
    if (role === 'investor') return '/investor/dashboard';
    if (role === 'founder') return '/founder/dashboard';
    return null;
  };

  const handleAuthClick = () => {
    const url = getDashboardUrl();
    router.push(url ?? '/auth/login');
  };

  const handleSignOut = async () => {
    await authClient.signOut();
    setSession(null);
    router.push('/auth/login');
  };

  return (
    <>
      <header
        className={cn(
          'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
          isScrolled
            ? 'bg-white/95 backdrop-blur-lg shadow-sm border-b border-gray-100/50'
            : 'bg-transparent'
        )}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <Logo imageClassName="h-8 lg:h-10 w-auto" />

            {/* Desktop Navigation */}
            <nav className="hidden xl:flex items-center gap-6 lg:gap-8">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className={cn(
                    'relative text-sm font-medium transition-colors group',
                    isScrolled
                      ? 'text-gray-600 hover:text-gray-900'
                      : 'text-gray-700 hover:text-gray-900'
                  )}
                >
                  {link.label}
                  {/* Animated underline */}
                  <span className="absolute left-0 -bottom-1 w-0 h-0.5 bg-gradient-to-r from-purple-600 to-violet-600 transition-all duration-300 group-hover:w-full" />
                </a>
              ))}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden xl:flex items-center gap-3 lg:gap-4">
              {!isCheckingSession && (
                <>
                  {session ? (
                    <>
                      {getDashboardUrl() && (
                        <button
                          onClick={handleAuthClick}
                          className={cn(
                            'text-sm font-medium transition-colors',
                            isScrolled
                              ? 'text-gray-600 hover:text-gray-900'
                              : 'text-gray-700 hover:text-gray-900'
                          )}
                        >
                          Dashboard
                        </button>
                      )}
                      <button
                        onClick={handleSignOut}
                        className={cn(
                          'text-sm font-medium transition-colors',
                          isScrolled
                            ? 'text-gray-400 hover:text-gray-600'
                            : 'text-gray-500 hover:text-gray-700'
                        )}
                      >
                        Sign Out
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={handleAuthClick}
                        className={cn(
                          'text-sm font-medium transition-colors',
                          isScrolled
                            ? 'text-gray-600 hover:text-gray-900'
                            : 'text-gray-700 hover:text-gray-900'
                        )}
                      >
                        Sign In
                      </button>
                      <a href="/apply">
                        <Button className="btn-premium text-white px-5 py-2 text-sm rounded-lg group overflow-hidden">
                          {/* Shimmer effect */}
                          <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                          <span className="relative flex items-center gap-2">
                            Apply for Access
                            <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                          </span>
                        </Button>
                      </a>
                    </>
                  )}
                </>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="xl:hidden w-10 h-10 flex items-center justify-center text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <span className="sr-only">Toggle menu</span>
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Full screen mobile menu */}
      <div
        className={cn(
          'fixed inset-0 z-40 xl:hidden transition-all duration-500',
          isMobileMenuOpen ? 'visible' : 'invisible'
        )}
      >
        {/* Backdrop */}
        <div
          className={cn(
            'absolute inset-0 bg-black/20 backdrop-blur-sm transition-opacity duration-500',
            isMobileMenuOpen ? 'opacity-100' : 'opacity-0'
          )}
          onClick={() => setIsMobileMenuOpen(false)}
        />

        {/* Menu panel */}
        <div
          className={cn(
            'absolute top-0 right-0 w-full max-w-sm h-full bg-white shadow-2xl transition-transform duration-500 ease-out',
            isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          )}
        >
          {/* Close button area */}
          <div className="h-16 flex items-center justify-end px-6 border-b border-gray-100">
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="w-10 h-10 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Menu content */}
          <div className="px-6 py-8">
            {/* Navigation links */}
            <nav className="space-y-2 mb-8">
              {navLinks.map((link, index) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={cn(
                    'block py-4 text-2xl font-display font-semibold text-gray-900 hover:text-purple-600 transition-all border-b border-gray-100',
                    'transform transition-all duration-300',
                    isMobileMenuOpen
                      ? 'translate-x-0 opacity-100'
                      : 'translate-x-8 opacity-0'
                  )}
                  style={{ transitionDelay: `${150 + index * 50}ms` }}
                >
                  {link.label}
                </a>
              ))}
            </nav>

            {/* Auth section */}
            {!isCheckingSession && (
              <div
                className={cn(
                  'space-y-6 transition-all duration-300',
                  isMobileMenuOpen
                    ? 'translate-y-0 opacity-100'
                    : 'translate-y-4 opacity-0'
                )}
                style={{ transitionDelay: '400ms' }}
              >
                {session ? (
                  <>
                    {getDashboardUrl() && (
                      <button
                        onClick={() => {
                          setIsMobileMenuOpen(false);
                          handleAuthClick();
                        }}
                        className="block w-full py-3 text-center text-gray-600 hover:text-gray-900 font-medium border border-gray-200 rounded-xl transition-colors"
                      >
                        Go to Dashboard
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        handleSignOut();
                      }}
                      className="block w-full py-3 text-center text-gray-400 hover:text-gray-600 font-medium border border-gray-100 rounded-xl transition-colors"
                    >
                      Sign Out
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        handleAuthClick();
                      }}
                      className="block w-full py-3 text-center text-gray-600 hover:text-gray-900 font-medium border border-gray-200 rounded-xl transition-colors"
                    >
                      Sign In
                    </button>
                    <a href="/apply" onClick={() => setIsMobileMenuOpen(false)}>
                      <Button className="w-full btn-premium text-white py-4 rounded-xl text-base font-semibold">
                        <span className="flex items-center justify-center gap-2">
                          Apply for Access
                          <ArrowRight className="w-4 h-4" />
                        </span>
                      </Button>
                    </a>
                  </>
                )}
              </div>
            )}

            {/* Bottom tagline */}
            <p
              className={cn(
                'mt-12 text-sm text-gray-400 text-center font-serif italic transition-all duration-300',
                isMobileMenuOpen
                  ? 'translate-y-0 opacity-100'
                  : 'translate-y-4 opacity-0'
              )}
              style={{ transitionDelay: '500ms' }}
            >
              "Designing fundable ventures"
            </p>
          </div>
        </div>
      </div>

      {/* Spacer */}
      <div className="h-16 lg:h-20" />
    </>
  );
}
